import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  Sparkles, Save, Copy, Trash2, RefreshCw, AlertTriangle, CheckCircle2, XCircle,
  Loader2, ExternalLink, MapPin, Search, ChevronRight, Download, Code2,
  Lock, Shuffle, FileText, Link2,
} from 'lucide-react';
import { apiFetch } from '../../utils/apiFetch';
import { indir, panoyaKopyala, markdownUret, duzMetinUret } from './icerikDisaAktar';

/**
 * Lokasyon Stüdyosu — ofis sayfalarının boş gövde metnini üretir.
 *
 * İçerik Stüdyosu'nun "Üret" sekmesinden farkı: orada konu serbest, burada sayfa
 * belli. Solda 78 ofis listelenir (metni olan / olmayan), ortada üretilen metin,
 * sağda denetim.
 *
 * 🔑 Şablon referans sayfadan çıkarıldı ama BÖLÜMLERİ değil BİÇİMİ alındı.
 * /konumlar/mugla/bodrum-milas-havalimani sitedeki tek dolu ofis sayfası (1.027
 * kelime) — ama içeriği bir ŞEHİR metni ("Bodrum'da Ne Yenir?") ve aynısı zaten
 * şehir sayfasında var. Klonlarsak İstanbul'un ofisleri birbirinin ve kendi şehir
 * sayfasının kopyası olur; denetimdeki iki çakışma kuralı tam da bunu ölçüyor.
 *
 * 🔑 Olgular modelden istenmiyor, modele veriliyor: adres/telefon/saat sayfanın
 * kendi AutoRental JSON-LD'sinden geliyor. Denetim geri ölçüyor — metinde olgu
 * setinde bulunmayan saat/tutar/mesafe varsa "Doğrulanmamış olgu" kalır.
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();
const API = `${API_BASE}/api/location-studio`;

const nf = (n) => Number(n || 0).toLocaleString('tr-TR');
const TIP_AD = {
  havalimani: 'Havalimanı', 'tren-gari': 'Tren garı', otogar: 'Otogar', avm: 'AVM',
  'sehir-merkez': 'Şehir merkez', 'ilce-tatil-ofisi': 'İlçe / tatil', sehir: 'Şehir sayfası',
};
// Müşteri onay akışı (2026-09-15). Kilit arayüzde değil uçta: onaydan geçmemiş
// taslak "yayında" işaretlenemiyor, uç 409 döndürüyor.
const DURUMLAR = [
  { k: 'taslak', ad: 'Taslak', renk: 'bg-slate-100 text-slate-600' },
  { k: 'incelemede', ad: 'İç inceleme', renk: 'bg-amber-100 text-amber-700' },
  { k: 'musteriye_gonderildi', ad: 'Müşteride', renk: 'bg-indigo-100 text-indigo-700' },
  { k: 'revizyon', ad: 'Revizyon', renk: 'bg-rose-100 text-rose-700' },
  { k: 'musteri_onayli', ad: 'Müşteri onayladı', renk: 'bg-emerald-100 text-emerald-700' },
  { k: 'yayinda', ad: 'Yayında', renk: 'bg-blue-100 text-blue-700' },
];
const durumBul = (k) => DURUMLAR.find((d) => d.k === k) || DURUMLAR[0];

// Künyedeki her alan ya doğrulanmış ya da müşteriden istenecek; arada "idare
// eder" yok — blog turunda tam da bu boşluklar sorun oldu.
const KAYNAK_ROZET = {
  dogrulandi: ['bg-emerald-100 text-emerald-700', 'Doğrulandı'],
  turetildi: ['bg-emerald-50 text-emerald-700', 'Türetildi'],
  yonlendirildi: ['bg-emerald-50 text-emerald-700', 'Koşullar sayfasına yönlendirildi'],
  jenerik: ['bg-amber-100 text-amber-700', 'Ofise özgü değil'],
  supheli: ['bg-amber-100 text-amber-700', 'Kaynak şüpheli'],
  teyitBekliyor: ['bg-amber-100 text-amber-700', 'Operasyon teyidi bekliyor'],
  eksik: ['bg-amber-100 text-amber-700', 'Kayıt eksik'],
  kaynakYok: ['bg-rose-100 text-rose-700', 'Kaynak yok'],
};

const SET_DURUMLARI = [
  { k: 'hazirlaniyor', ad: 'Hazırlanıyor' },
  { k: 'gonderildi', ad: 'Müşteriye gönderildi' },
  { k: 'revizyon', ad: 'Revizyon istendi' },
  { k: 'onaylandi', ad: 'Onaylandı' },
];

function Kutu({ baslik, sag, children, className = '' }) {
  return (
    <section className={`rounded-xl border border-slate-200 bg-white ${className}`}>
      {(baslik || sag) && (
        <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-2.5">
          <h3 className="text-[12px] font-bold uppercase tracking-wider text-slate-700">{baslik}</h3>
          {sag}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

// Kontrollü alan; dışarıdaki değeri YALNIZCA odak dışındayken alır, yoksa her
// tuşta üstteki state yeniden yazılıp imleç başa dönüyor.
function Alan({ deger, onIsle, satir = 3, className = '' }) {
  const [t, setT] = useState(deger ?? '');
  const [odakta, setOdakta] = useState(false);
  useEffect(() => { if (!odakta) setT(deger ?? ''); }, [deger, odakta]);
  return (
    <textarea
      rows={satir}
      value={t}
      onChange={(e) => setT(e.target.value)}
      onFocus={() => setOdakta(true)}
      onBlur={() => { setOdakta(false); onIsle(t); }}
      className={`w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-[13px] leading-relaxed text-slate-800 outline-none focus:border-emerald-400 ${className}`}
    />
  );
}

export default function LokasyonStudyosu({ brandId, headers }) {
  const [boot, setBoot] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [tariyor, setTariyor] = useState(false);

  const [seciliUrl, setSeciliUrl] = useState(null);
  const [brief, setBrief] = useState(null);
  const [briefYukleniyor, setBriefYukleniyor] = useState(false);

  const [icerik, setIcerik] = useState(null);
  const [denetim, setDenetim] = useState(null);
  const [ciktiHtml, setCiktiHtml] = useState('');
  const [jsonLd, setJsonLd] = useState(null);
  const [model, setModel] = useState(null);
  const [taslakId, setTaslakId] = useState(null);
  const [uretiliyor, setUretiliyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);

  const [kunye, setKunye] = useState(null);
  const [numune, setNumune] = useState(null);
  const [numuneKuruluyor, setNumuneKuruluyor] = useState(false);
  const [paketAliniyor, setPaketAliniyor] = useState(false);
  const [kayitDurumu, setKayitDurumu] = useState('taslak');
  // Künye elle düzenlendiğinde denetim yeniden çalışsın diye sayaç; künyenin
  // kendisi bağımlılığa konursa cevap→state→istek döngüsü kuruluyor.
  const [kunyeElle, setKunyeElle] = useState(0);

  const [ara, setAra] = useState('');
  const [suzgec, setSuzgec] = useState('bos'); // bos | hepsi | yazilan
  const denetimZaman = useRef(null);
  // Denetim isteği künyeyi de taşıyor (elle yazılan hedef sorgu ezilmesin);
  // effect'in bağımlılığına koymamak için ref'ten okunuyor.
  const kunyeRef = useRef(null);
  useEffect(() => { kunyeRef.current = kunye; }, [kunye]);

  /* ───────────────────────────── yükleme ───────────────────────────── */

  const yukle = useCallback(async () => {
    if (!brandId) { setHata('Marka seçili değil.'); setYukleniyor(false); return; }
    setYukleniyor(true);
    try {
      const res = await apiFetch(`${API}/bootstrap?_t=${Date.now()}`, { headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Lokasyon stüdyosu yüklenemedi');
      setBoot(json);
      setHata(null);
    } catch (e) { setHata(e.message); } finally { setYukleniyor(false); }
  }, [brandId, headers]);

  useEffect(() => { yukle(); }, [yukle]);

  const tara = async () => {
    setTariyor(true);
    try {
      const res = await apiFetch(`${API}/refresh`, { method: 'POST', headers, body: JSON.stringify({}) });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || 'Tarama başarısız');
      toast.success(`${json.tarandi} sayfa tarandı · ${json.ofis} ofis, ${json.metniOlan}'inde metin var`);
      if (json.saatUyarisi) toast(json.saatUyarisi, { icon: '⚠️', duration: 9000 });
      await yukle();
    } catch (e) { toast.error(e.message); } finally { setTariyor(false); }
  };

  /* ───────────────────────── sayfa seçimi & brief ──────────────────── */

  const ofisler = useMemo(() => (boot?.envanter || []).filter((x) => x.seviye === 'ofis'), [boot]);
  const taslakIndeksi = useMemo(() => {
    const m = new Map();
    (boot?.taslaklar || []).forEach((t) => { if (!m.has(t.url)) m.set(t.url, t); });
    return m;
  }, [boot]);

  const liste = useMemo(() => {
    const a = ara.trim().toLowerCase();
    return ofisler
      .filter((o) => {
        if (suzgec === 'bos' && o.govdeKelime > 0) return false;
        if (suzgec === 'yazilan' && !taslakIndeksi.has(o.url)) return false;
        if (!a) return true;
        return `${o.ofisAd} ${o.sehir} ${o.url}`.toLowerCase().includes(a);
      })
      .sort((x, y) => String(x.sehir).localeCompare(String(y.sehir), 'tr')
        || String(x.ofisAd).localeCompare(String(y.ofisAd), 'tr'));
  }, [ofisler, ara, suzgec, taslakIndeksi]);

  const sayfaSec = async (url) => {
    setSeciliUrl(url);
    setIcerik(null); setDenetim(null); setCiktiHtml(''); setJsonLd(null); setTaslakId(null);
    setKunye(null); setKayitDurumu('taslak');
    setBriefYukleniyor(true);
    try {
      const res = await apiFetch(`${API}/brief?url=${encodeURIComponent(url)}`, { headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Brief alınamadı');
      setBrief(json);
      // Bu sayfa için kayıtlı taslak varsa doğrudan aç — iki kez üretmeyelim.
      if (json.mevcutTaslak?.id) await taslakAc(json.mevcutTaslak.id, false);
    } catch (e) { toast.error(e.message); setBrief(null); } finally { setBriefYukleniyor(false); }
  };

  const taslakAc = async (id, bildir = true) => {
    try {
      const res = await apiFetch(`${API}/drafts/${id}`, { headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Taslak açılamadı');
      setSeciliUrl(json.url); setIcerik(json.icerik); setDenetim(json.denetim);
      setCiktiHtml(json.html || ''); setJsonLd(json.jsonLd || null);
      setModel(json.model); setTaslakId(json.id);
      setKunye(json.kunye || null); setKayitDurumu(json.durum || 'taslak');
      if (bildir) toast.success('Taslak açıldı');
    } catch (e) { toast.error(e.message); }
  };

  /* ───────────────────────────── üretim ────────────────────────────── */

  const uret = async () => {
    if (!seciliUrl) return;
    setUretiliyor(true);
    try {
      const res = await apiFetch(`${API}/generate`, {
        method: 'POST', headers,
        body: JSON.stringify({ url: seciliUrl, kunye: kunyeRef.current || undefined }),
      });
      // 🔑 Uzun uç: ilk bayt nabızla akıyor, artık HTTP durumu değişemiyor.
      // Hata da 200 gövdesinde {error} olarak geliyor — ikisine birden bak.
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error || 'Üretim başarısız');
      setIcerik(json.icerik); setDenetim(json.denetim); setModel(json.model);
      setCiktiHtml(json.html || ''); setJsonLd(json.jsonLd || null); setTaslakId(null);
      setKunye(json.kunye || null); setKayitDurumu('taslak');
      if (json.saatlerKullanildi === false) {
        toast('Çalışma saatleri her ofiste aynı geldiği için metne saat yazdırılmadı.', { icon: 'ℹ️', duration: 7000 });
      }
      toast.success(`${json.denetim?.kelime || 0} kelime · denetim ${json.denetim?.puan ?? '-'}/100`);
    } catch (e) { toast.error(e.message); } finally { setUretiliyor(false); }
  };

  // Elle düzenlemeden sonra denetim yeniden çalışsın; modele gitmiyor, sadece
  // metni ölçüyor. 900 ms gecikme her tuşta istek atmasın diye.
  useEffect(() => {
    if (!icerik || !seciliUrl) return;
    clearTimeout(denetimZaman.current);
    denetimZaman.current = setTimeout(async () => {
      try {
        const res = await apiFetch(`${API}/audit`, {
          method: 'POST', headers,
          body: JSON.stringify({ url: seciliUrl, icerik, kunye: kunyeRef.current || undefined }),
        });
        const json = await res.json();
        if (res.ok) {
          setDenetim(json.denetim); setCiktiHtml(json.html || ''); setJsonLd(json.jsonLd || null);
          if (json.kunye) setKunye(json.kunye);
        }
      } catch { /* denetim sessizce atlanır, üretim bozulmasın */ }
    }, 900);
    return () => clearTimeout(denetimZaman.current);
  }, [icerik, seciliUrl, headers, kunyeElle]);

  const kaydet = async (durum) => {
    if (!icerik || !seciliUrl) return;
    setKaydediliyor(true);
    try {
      const numuneId = numune?.secim?.some((x) => x.url === seciliUrl) ? numune.id : null;
      const res = await apiFetch(`${API}/drafts`, {
        method: 'POST', headers,
        body: JSON.stringify({
          id: taslakId, url: seciliUrl, icerik, denetim, model, durum, kunye, numuneId,
        }),
      });
      const json = await res.json();
      // 409 = yayın kilidi. Mesaj uçtan geliyor, burada tekrar yazılmıyor.
      if (!res.ok) throw new Error(json.error || 'Kaydedilemedi');
      setTaslakId(json.id); setKayitDurumu(json.durum || durum);
      toast.success(`Kaydedildi · ${durumBul(json.durum || durum).ad}`);
      await yukle();
      if (numune?.id) await numuneYukle(numune.id, false);
    } catch (e) { toast.error(e.message, { duration: 8000 }); } finally { setKaydediliyor(false); }
  };

  /* ─────────────────── örnek set (müşteri onayı) ───────────────────── */

  const numuneYukle = useCallback(async (id, bildir = true) => {
    try {
      const res = await apiFetch(`${API}/numune/${id}`, { headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Set okunamadı');
      setNumune(json);
      if (bildir) toast.success(`${json.secim.length} istasyonluk set açıldı`);
    } catch (e) { if (bildir) toast.error(e.message); }
  }, [headers]);

  const numuneOlustur = async () => {
    if (numune && !window.confirm('Yeni bir örnek set oluşturulsun mu? Mevcut set listede kalır.')) return;
    setNumuneKuruluyor(true);
    try {
      const res = await apiFetch(`${API}/numune`, {
        method: 'POST', headers,
        body: JSON.stringify({ adet: 10, tohum: String(Date.now()) }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Set oluşturulamadı');
      (json.uyarilar || []).forEach((u) => toast(u, { icon: '⚠️', duration: 8000 }));
      toast.success(`${json.dagilim.adet} istasyon · ${json.dagilim.sehir} şehir · ${json.dagilim.havalimani} havalimanı`);
      await yukle();
      await numuneYukle(json.id, false);
    } catch (e) { toast.error(e.message); } finally { setNumuneKuruluyor(false); }
  };

  const paketIndir = async () => {
    if (!numune?.id) return;
    setPaketAliniyor(true);
    try {
      const res = await apiFetch(`${API}/numune/${numune.id}/paket`, { headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Paket üretilemedi');
      indir(json.html, json.dosyaAdi || 'ornek-set.html', 'text/html;charset=utf-8');
      toast.success(`Paket indirildi · set içi en yüksek kopya oranı %${(json.enYuksekKopya || 0).toFixed(1)}`);
    } catch (e) { toast.error(e.message, { duration: 9000 }); } finally { setPaketAliniyor(false); }
  };

  const numuneDurumYaz = async (durum) => {
    if (!numune?.id) return;
    try {
      const res = await apiFetch(`${API}/numune/${numune.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ durum }),
      });
      if (!res.ok) throw new Error('Set durumu yazılamadı');
      setNumune((o) => ({ ...o, durum }));
      await yukle();
    } catch (e) { toast.error(e.message); }
  };

  // Açılışta en son set kendiliğinden gelsin — iş bu setin etrafında dönüyor.
  useEffect(() => {
    const ilk = boot?.numuneler?.[0];
    if (ilk && !numune) numuneYukle(ilk.id, false);
  }, [boot, numune, numuneYukle]);

  const taslakSil = async (id) => {
    if (!window.confirm('Taslak silinsin mi?')) return;
    try {
      const res = await apiFetch(`${API}/drafts/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Silinemedi');
      if (id === taslakId) { setTaslakId(null); setIcerik(null); setDenetim(null); }
      toast.success('Silindi'); await yukle();
    } catch (e) { toast.error(e.message); }
  };

  /* ──────────────────────────── dışa aktarım ──────────────────────── */

  // Üret sekmesiyle aynı çubuk, aynı biçimler. Tek fark: buradaki "HTML" site
  // CMS'inin zengin metin kalıbı (uçtan gelen `ciktiHtml`) — ofis sayfaları o
  // kalıpla yazılmış, düz <h2>/<p> temada farklı görünüyor.
  //
  // 🔑 Ortak dışa aktarıcılar `maddeler`i düz metin dizisi bekliyor; burada ise
  // {metin, url} nesnesi (yakın ofis listesi). Nesne olduğu gibi geçerse
  // Markdown'a "[object Object]" düşer, o yüzden burada düzleştiriliyor.
  const disaIcerik = useMemo(() => {
    if (!icerik) return null;
    const o = brief?.olgular || {};
    const katla = (t) => String(t || '').toLocaleLowerCase('tr').replace(/\s+/g, ' ').trim();
    const baslik = o.ofisAd || o.h1 || '';
    return {
      baslik,
      slug: o.ofisSlug || '',
      metaBaslik: icerik.metaBaslik,
      metaAciklama: icerik.metaAciklama,
      girisParagraflari: [],
      bolumler: (icerik.bolumler || []).map((b, i) => ({
        // İlk bölümün H2'si ofis adının kendisi: md/düz metinde başlıkla üst
        // üste binmesin (CMS bloğunda kalıyor, orada H2 şart).
        h2: i === 0 && katla(b.h2) === katla(baslik) ? '' : b.h2,
        paragraflar: b.paragraflar || [],
        maddeler: (b.maddeler || [])
          .map((m) => (m?.url ? `${m.metin} — ${m.url}` : m?.metin || ''))
          .filter(Boolean),
      })),
      sss: icerik.sss || [],
      icLinkler: (kunye?.icLinkler || icerik.icLinkler || []),
      notlar: icerik.notlar || [],
    };
  }, [icerik, brief, kunye]);

  const ustveri = useMemo(() => ({
    marka: boot?.marka,
    dil: 'tr',
    durum: durumBul(kayitDurumu).ad,
    odakKelime: kunye?.hedefSorgu,
    kelimeSayisi: denetim?.kelime,
    model,
  }), [boot, kayitDurumu, kunye, denetim, model]);

  const disaAktar = async (bicim) => {
    if (!icerik || !disaIcerik) return;
    const taban = brief?.olgular?.ofisSlug || 'ofis';
    if (bicim === 'md') {
      indir(markdownUret(disaIcerik, ustveri), `${taban}.md`, 'text/markdown;charset=utf-8');
    }
    if (bicim === 'html') {
      indir(ciktiHtml, `${taban}.html`, 'text/html;charset=utf-8');
    }
    if (bicim === 'json') {
      indir(
        JSON.stringify({ ustveri, url: seciliUrl, olgular: brief?.olgular, kunye, icerik, denetim, html: ciktiHtml, jsonLd }, null, 2),
        `${taban}.json`, 'application/json'
      );
    }
    if (bicim === 'kopyala-duz') {
      await panoyaKopyala(duzMetinUret(disaIcerik)); toast.success('Düz metin panoya kopyalandı');
    }
    if (bicim === 'kopyala-md') {
      await panoyaKopyala(markdownUret(disaIcerik, ustveri)); toast.success('Markdown panoya kopyalandı');
    }
    if (bicim === 'kopyala-html') {
      await panoyaKopyala(ciktiHtml); toast.success('HTML (CMS bloğu) panoya kopyalandı');
    }
    if (bicim === 'kopyala-jsonld') {
      if (!jsonLd) { toast.error('SSS olmadığı için FAQPage şeması üretilmedi'); return; }
      await panoyaKopyala(jsonLd); toast.success('FAQPage JSON-LD panoya kopyalandı');
    }
  };

  /* ───────────────────────── içerik düzenleme ──────────────────────── */

  const kunyeGuncelle = (yama) => {
    setKunye((o) => ({ ...(o || {}), ...yama }));
    setKunyeElle((n) => n + 1);
  };
  const yerlesimGuncelle = (yama) => {
    setKunye((o) => ({ ...(o || {}), yerlesim: { ...(o?.yerlesim || {}), ...yama } }));
    setKunyeElle((n) => n + 1);
  };

  const bolumGuncelle = (i, alan, deger) => setIcerik((o) => {
    const y = { ...o, bolumler: [...(o.bolumler || [])] };
    y.bolumler[i] = { ...y.bolumler[i], [alan]: deger };
    return y;
  });
  const sssGuncelle = (i, alan, deger) => setIcerik((o) => {
    const y = { ...o, sss: [...(o.sss || [])] };
    y.sss[i] = { ...y.sss[i], [alan]: deger };
    return y;
  });

  /* ───────────────────────────── render ────────────────────────────── */

  if (yukleniyor) {
    return <div className="flex items-center gap-2 py-16 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…</div>;
  }
  if (hata) {
    return <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-[13px] text-rose-700">{hata}</div>;
  }

  const ozet = boot?.ozet || {};
  const envanterBos = !ofisler.length;

  return (
    <div className="mt-5">
      {/* ── Üst şerit ── */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[13px] text-slate-600">
          <span><strong className="font-semibold text-slate-800">{nf(ozet.ofis)}</strong> ofis sayfası</span>
          <span className="text-rose-600"><strong className="font-semibold">{nf(ozet.metniOlmayan)}</strong> metni yok</span>
          <span className="text-emerald-700"><strong className="font-semibold">{nf(ozet.metniOlan)}</strong> metni var</span>
          <span><strong className="font-semibold text-slate-800">{nf(boot?.taslaklar?.length)}</strong> taslak yazıldı</span>
        </div>
        <button
          onClick={tara}
          disabled={tariyor}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {tariyor ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {tariyor ? 'Taranıyor…' : 'Sayfaları tara'}
        </button>
      </div>

      {/* ── Müşteri onay şeridi ── */}
      <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-amber-900">
                Yayın kilidi açık — müşteri örnek seti onaylamadan canlı sayfaya içerik girilmiyor
              </p>
              <p className="mt-0.5 text-[12px] leading-relaxed text-amber-800">
                Müşteri önce 10 farklı istasyondan örnek metin, her biriyle beraber sayfa linki,
                metnin gireceği alan, hedef sorgu, SEO alanları, iç linkler ve bilgilerin hangi
                kaynakla doğrulandığını istiyor. Bir taslak ancak <strong>“Müşteri onayladı”</strong>
                {' '}işaretlendikten sonra “Yayında” yapılabiliyor.
              </p>
            </div>
          </div>
          {!numune && (
            <button
              onClick={numuneOlustur}
              disabled={numuneKuruluyor || envanterBos}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-2 text-[13px] font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {numuneKuruluyor ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Shuffle className="h-3.5 w-3.5" />}
              10 istasyonluk örnek set oluştur
            </button>
          )}
        </div>

        {numune && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-white p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-[12px] text-slate-600">
                <strong className="text-slate-800">{numune.ad}</strong>
                <span className="mx-2 text-slate-300">|</span>
                {numune.dagilim?.sehir} farklı şehir · {numune.dagilim?.havalimani} havalimanı ·
                {' '}{numune.dagilim?.sehirIci} şehir içi
                <span className="mx-2 text-slate-300">|</span>
                {numune.secim.filter((x) => x.taslakId).length}/{numune.secim.length} metin hazır
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <select
                  value={numune.durum}
                  onChange={(e) => numuneDurumYaz(e.target.value)}
                  className="rounded-lg border border-slate-200 px-2 py-1.5 text-[12px] text-slate-700 outline-none focus:border-amber-400"
                >
                  {SET_DURUMLARI.map((d) => <option key={d.k} value={d.k}>{d.ad}</option>)}
                </select>
                <button
                  onClick={paketIndir}
                  disabled={paketAliniyor}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {paketAliniyor ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                  Müşteri paketini indir
                </button>
                <button
                  onClick={numuneOlustur}
                  disabled={numuneKuruluyor}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                ><Shuffle className="h-3.5 w-3.5" /> Yeni set</button>
              </div>
            </div>

            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {numune.secim.map((o, i) => {
                const d = o.taslakDurum ? durumBul(o.taslakDurum) : null;
                return (
                  <button
                    key={o.url}
                    onClick={() => sayfaSec(o.url)}
                    title={o.url}
                    className={`flex items-center gap-1.5 rounded-lg border px-2 py-1 text-[12px] transition ${
                      o.url === seciliUrl ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <span className="text-slate-400">{i + 1}</span>
                    <span className="font-medium text-slate-700">{o.ofisAd}</span>
                    <span className="text-slate-400">{o.sehir}</span>
                    {d
                      ? <span className={`rounded px-1 py-px text-[11px] ${d.renk}`}>{d.ad}{o.puan != null ? ` ${o.puan}` : ''}</span>
                      : <span className="rounded bg-rose-50 px-1 py-px text-[11px] text-rose-600">metin yok</span>}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
              Seçim rastgele ama körlemesine değil: her istasyon farklı şehirden ve set
              havalimanı/şehir içi karışımı olacak şekilde kotalı seçiliyor. Tohum kayıtlı,
              aynı set istendiğinde birebir yeniden üretilebiliyor.
            </p>
          </div>
        )}
      </div>

      {envanterBos && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-[13px] leading-relaxed text-amber-800">
          Envanter boş. <strong className="font-semibold">Sayfaları tara</strong> dediğinde sitemap'teki
          <code className="mx-1 rounded bg-amber-100 px-1">/konumlar/</code> sayfaları taranır; adres, koordinat,
          telefon ve çalışma saatleri her sayfanın kendi <code className="rounded bg-amber-100 px-1">AutoRental</code>
          {' '}JSON-LD'sinden okunur. Model bu olguları uydurmasın diye önce bu adım gerekiyor.
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[320px_minmax(0,1fr)_300px]">
        {/* ══════════ Sol: ofis listesi ══════════ */}
        <div className="space-y-3">
          <Kutu baslik="Ofis sayfaları">
            <div className="relative mb-2">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <input
                value={ara}
                onChange={(e) => setAra(e.target.value)}
                placeholder="Ofis veya şehir ara…"
                className="w-full rounded-lg border border-slate-200 py-2 pl-8 pr-3 text-[13px] outline-none focus:border-emerald-400"
              />
            </div>
            <div className="mb-3 flex gap-1">
              {[
                { k: 'bos', ad: `Metni yok (${nf(ozet.metniOlmayan)})` },
                { k: 'yazilan', ad: `Yazılan (${nf(boot?.taslaklar?.length)})` },
                { k: 'hepsi', ad: `Hepsi (${nf(ozet.ofis)})` },
              ].map((s) => (
                <button
                  key={s.k}
                  onClick={() => setSuzgec(s.k)}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium transition ${
                    suzgec === s.k ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >{s.ad}</button>
              ))}
            </div>

            <div className="max-h-[560px] space-y-0.5 overflow-y-auto">
              {!liste.length && <p className="py-6 text-center text-[13px] text-slate-400">Eşleşen sayfa yok.</p>}
              {liste.map((o) => {
                const t = taslakIndeksi.get(o.url);
                const aktif = o.url === seciliUrl;
                return (
                  <button
                    key={o.url}
                    onClick={() => sayfaSec(o.url)}
                    className={`flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left transition ${
                      aktif ? 'bg-emerald-50 ring-1 ring-emerald-200' : 'hover:bg-slate-50'
                    }`}
                  >
                    <MapPin className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${aktif ? 'text-emerald-600' : 'text-slate-400'}`} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-medium text-slate-800">{o.ofisAd || o.h1}</span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
                        <span>{o.sehir}</span>
                        <span className="rounded bg-slate-100 px-1 py-px">{TIP_AD[o.tip] || o.tip}</span>
                        {o.govdeKelime > 0 && <span className="text-emerald-600">{nf(o.govdeKelime)} kel.</span>}
                        {t && (
                          <span className={`rounded px-1 py-px ${(DURUMLAR.find((d) => d.k === t.durum) || DURUMLAR[0]).renk}`}>
                            {(DURUMLAR.find((d) => d.k === t.durum) || DURUMLAR[0]).ad}
                            {t.puan != null ? ` ${t.puan}` : ''}
                          </span>
                        )}
                      </span>
                    </span>
                    <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-slate-300" />
                  </button>
                );
              })}
            </div>
          </Kutu>
        </div>

        {/* ══════════ Orta: brief + metin ══════════ */}
        <div className="space-y-4">
          {!seciliUrl && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
              <MapPin className="mx-auto h-6 w-6 text-slate-300" />
              <p className="mt-2 text-[13px] font-medium text-slate-700">Soldan bir ofis sayfası seç</p>
              <p className="mt-1 text-[13px] leading-relaxed text-slate-500">
                Her sayfanın kendi adresi, koordinatı ve o sayfaya inen gerçek arama sorguları
                brief olarak gelir; metin bunların üstüne yazılır.
              </p>
            </div>
          )}

          {seciliUrl && briefYukleniyor && (
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-6 text-[13px] text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Brief hazırlanıyor…
            </div>
          )}

          {seciliUrl && brief && !briefYukleniyor && (
            <Kutu
              baslik="Brief"
              sag={
                <a href={brief.olgular.url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-500 hover:text-emerald-700">
                  Sayfayı aç <ExternalLink className="h-3 w-3" />
                </a>
              }
            >
              <dl className="grid gap-x-4 gap-y-2 text-[13px] sm:grid-cols-2">
                <div><dt className="text-[11px] uppercase tracking-wide text-slate-400">Ofis</dt>
                  <dd className="font-medium text-slate-800">{brief.olgular.ofisAd}</dd></div>
                <div><dt className="text-[11px] uppercase tracking-wide text-slate-400">Tip / şehir</dt>
                  <dd className="text-slate-700">{TIP_AD[brief.olgular.tip] || brief.olgular.tip} · {brief.olgular.sehir}</dd></div>
                <div className="sm:col-span-2"><dt className="text-[11px] uppercase tracking-wide text-slate-400">Adres (olgu)</dt>
                  <dd className="text-slate-700">{brief.olgular.adres || <span className="text-rose-600">JSON-LD'de adres yok</span>}</dd></div>
                <div><dt className="text-[11px] uppercase tracking-wide text-slate-400">Aynı şehirde</dt>
                  <dd className="text-slate-700">{brief.kardesler.length} ofis daha</dd></div>
                <div><dt className="text-[11px] uppercase tracking-wide text-slate-400">Mevcut gövde</dt>
                  <dd className={brief.olgular.govdeKelime ? 'text-emerald-700' : 'text-rose-600'}>
                    {brief.olgular.govdeKelime ? `${nf(brief.olgular.govdeKelime)} kelime var` : 'metin yok'}
                  </dd></div>
              </dl>

              {brief.sorgular?.length > 0 ? (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <p className="mb-1.5 text-[11px] uppercase tracking-wide text-slate-400">
                    Bu sayfaya inen aramalar · son {brief.gun} gün
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {brief.sorgular.slice(0, 14).map((s) => (
                      <span key={s.sorgu} className="rounded-md bg-slate-50 px-2 py-1 text-[12px] text-slate-600 ring-1 ring-slate-200">
                        {s.sorgu} <span className="text-slate-400">· {nf(s.gosterim)}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="mt-3 border-t border-slate-100 pt-3 text-[12px] text-amber-700">
                  Bu sayfaya inen sorgu bulunamadı — SSS veriden değil şablondan türeyecek.
                </p>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  onClick={uret}
                  disabled={uretiliyor || !boot?.geminiHazir}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {uretiliyor ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {uretiliyor ? 'Yazılıyor… (2-3 dk)' : icerik ? 'Yeniden üret' : 'Metni üret'}
                </button>
                {brief.mevcutTaslak?.id && (
                  <span className="text-[12px] text-slate-500">
                    Bu sayfa için kayıtlı taslak var ({nf(brief.mevcutTaslak.kelime_sayisi)} kelime).
                  </span>
                )}
              </div>
            </Kutu>
          )}

          {icerik && (
            <>
              {/* Eylem çubuğu — Üret sekmesindekiyle aynı: kaydet + indir + kopyala */}
              <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white/95 p-2.5 backdrop-blur">
                <button
                  onClick={() => kaydet(kayitDurumu)}
                  disabled={kaydediliyor}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-[13px] font-semibold text-white hover:bg-slate-700 disabled:opacity-40"
                >
                  {kaydediliyor ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  {taslakId ? 'Güncelle' : 'Arşive kaydet'}
                </button>
                <span className="mx-1 h-5 w-px bg-slate-200" />
                {[
                  ['md', 'Markdown'], ['html', 'HTML'], ['json', 'JSON'],
                ].map(([k, ad]) => (
                  <button
                    key={k}
                    onClick={() => disaAktar(k)}
                    title={`${ad} indir`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-2 text-[12px] font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <Download className="h-3.5 w-3.5" /> {ad}
                  </button>
                ))}
                <span className="mx-1 h-5 w-px bg-slate-200" />
                {[
                  ['kopyala-duz', 'Düz'], ['kopyala-md', 'MD'], ['kopyala-html', 'HTML'], ['kopyala-jsonld', 'JSON-LD'],
                ].map(([k, ad]) => (
                  <button
                    key={k}
                    onClick={() => disaAktar(k)}
                    title={k === 'kopyala-html'
                      ? 'CMS bloğunu (h2>strong, p>span) panoya kopyala'
                      : `${ad} panoya kopyala`}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-2 text-[12px] font-medium text-slate-600 hover:bg-slate-50"
                  >
                    <Copy className="h-3.5 w-3.5" /> {ad}
                  </button>
                ))}
                {model ? <span className="ml-auto text-[11px] text-slate-400">{model}</span> : null}
              </div>

              <Kutu baslik="Meta">
                <label className="mb-1 block text-[11px] uppercase tracking-wide text-slate-400">Meta başlık</label>
                <Alan satir={1} deger={icerik.metaBaslik} onIsle={(v) => setIcerik((o) => ({ ...o, metaBaslik: v }))} />
                <label className="mb-1 mt-3 block text-[11px] uppercase tracking-wide text-slate-400">Meta açıklama</label>
                <Alan satir={2} deger={icerik.metaAciklama} onIsle={(v) => setIcerik((o) => ({ ...o, metaAciklama: v }))} />
              </Kutu>

              {/* ── Teslim künyesi: müşteriye metinle BİRLİKTE giden bilgiler ── */}
              {kunye && (
                <Kutu
                  baslik="Teslim künyesi"
                  sag={<span className="text-[11px] text-slate-400">müşteriye metinle birlikte gidiyor</span>}
                >
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-[11px] uppercase tracking-wide text-slate-400">Hedeflenen ana sorgu</label>
                      <Alan satir={1} deger={kunye.hedefSorgu} onIsle={(v) => kunyeGuncelle({ hedefSorgu: v })} />
                      <p className="mt-1 text-[11px] leading-relaxed text-slate-500">{kunye.hedefSorguKaynak}</p>
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] uppercase tracking-wide text-slate-400">Mevcut içerik değişecek mi?</label>
                      <div className="flex gap-1">
                        {[{ v: false, ad: 'Hayır — boş alana eklenir' }, { v: true, ad: 'Evet — yerine geçer' }].map((x) => (
                          <button
                            key={String(x.v)}
                            onClick={() => yerlesimGuncelle({ degisecekMi: x.v })}
                            className={`rounded-md px-2 py-1.5 text-[12px] font-medium transition ${
                              !!kunye.yerlesim?.degisecekMi === x.v
                                ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >{x.ad}</button>
                        ))}
                      </div>
                      <p className="mt-1 text-[11px] leading-relaxed text-slate-500">{kunye.yerlesim?.mevcutIcerik}</p>
                    </div>
                  </div>

                  <label className="mb-1 mt-3 block text-[11px] uppercase tracking-wide text-slate-400">Metnin gireceği alan</label>
                  <Alan satir={2} deger={kunye.yerlesim?.alan} onIsle={(v) => yerlesimGuncelle({ alan: v })} />
                  <label className="mb-1 mt-3 block text-[11px] uppercase tracking-wide text-slate-400">Uygulama notu</label>
                  <Alan satir={2} deger={kunye.yerlesim?.aciklama} onIsle={(v) => yerlesimGuncelle({ aciklama: v })} />

                  {/* iç linkler */}
                  <div className="mt-4 border-t border-slate-100 pt-3">
                    <p className="mb-1.5 flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-400">
                      <Link2 className="h-3 w-3" /> İç bağlantılar ({(kunye.icLinkler || []).length})
                    </p>
                    {(kunye.icLinkler || []).length ? (
                      <ul className="space-y-1">
                        {kunye.icLinkler.map((l, i) => (
                          <li key={i} className="flex flex-wrap items-baseline gap-1.5 text-[12px]">
                            <span className="font-medium text-slate-700">{l.ankor}</span>
                            <span className="text-slate-400">{l.url}</span>
                            <span className={`rounded px-1 py-px text-[11px] ${
                              l.tip?.startsWith('Havuz dışı') ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'
                            }`}>{l.tip}</span>
                          </li>
                        ))}
                      </ul>
                    ) : <p className="text-[12px] text-amber-700">Metinde iç bağlantı yok.</p>}
                  </div>

                  {/* kaynak künyesi */}
                  <div className="mt-4 border-t border-slate-100 pt-3">
                    <p className="mb-1.5 text-[11px] uppercase tracking-wide text-slate-400">Bilgiler hangi kaynakla doğrulandı?</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[12px]">
                        <tbody>
                          {(kunye.kaynaklar || []).map((k, i) => {
                            const [renk, ad] = KAYNAK_ROZET[k.durum] || ['bg-slate-100 text-slate-600', k.durum];
                            return (
                              <tr key={i} className="border-b border-slate-50 align-top">
                                <td className="py-1.5 pr-2 font-medium text-slate-700">{k.alan}</td>
                                <td className="py-1.5 pr-2 text-slate-600">{k.deger || '—'}</td>
                                <td className="py-1.5 pr-2 text-slate-500">{k.kaynak}</td>
                                <td className="py-1.5"><span className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium ${renk}`}>{ad}</span></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {(kunye.eksikler || []).length > 0 && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <p className="text-[12px] font-semibold text-amber-900">
                        Müşteriden istenecek {kunye.eksikler.length} bilgi
                      </p>
                      <ul className="mt-1 space-y-0.5 text-[12px] leading-relaxed text-amber-800">
                        {kunye.eksikler.map((e, i) => <li key={i}>• <strong>{e.alan}:</strong> {e.not}</li>)}
                      </ul>
                      <p className="mt-1.5 text-[11px] text-amber-700">
                        Bu alanlar sitede ve yapısal veride yok; metinde bilinçli olarak yazılmadı.
                        Pakete “sizden istediğimiz bilgiler” başlığıyla giriyor.
                      </p>
                    </div>
                  )}
                </Kutu>
              )}

              {(icerik.bolumler || []).map((b, i) => (
                <Kutu key={`${b.rol}-${i}`} baslik={`${i + 1}. ${b.rol}`}
                  sag={<span className="text-[11px] text-slate-400">{(b.paragraflar || []).length} paragraf</span>}>
                  <Alan satir={1} deger={b.h2} onIsle={(v) => bolumGuncelle(i, 'h2', v)} className="font-semibold" />
                  <Alan
                    satir={Math.max(4, (b.paragraflar || []).join('\n\n').split('\n').length)}
                    deger={(b.paragraflar || []).join('\n\n')}
                    onIsle={(v) => bolumGuncelle(i, 'paragraflar', v.split(/\n{2,}/).map((x) => x.trim()).filter(Boolean))}
                    className="mt-2"
                  />
                  {(b.maddeler || []).length > 0 && (
                    <ul className="mt-2 space-y-1 rounded-lg bg-slate-50 p-2.5">
                      {b.maddeler.map((m, j) => (
                        <li key={j} className="text-[12px] text-slate-600">
                          {m.metin}{m.url && <span className="ml-1 text-slate-400">→ {m.url}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </Kutu>
              ))}

              {(icerik.sss || []).length > 0 && (
                <Kutu baslik={`SSS (${icerik.sss.length})`}
                  sag={jsonLd ? <span className="text-[11px] text-emerald-600">FAQPage şeması hazır</span> : null}>
                  {icerik.sss.map((s, i) => (
                    <div key={i} className={i ? 'mt-3 border-t border-slate-100 pt-3' : ''}>
                      <Alan satir={1} deger={s.soru} onIsle={(v) => sssGuncelle(i, 'soru', v)} className="font-medium" />
                      <Alan satir={2} deger={s.cevap} onIsle={(v) => sssGuncelle(i, 'cevap', v)} className="mt-1.5" />
                    </div>
                  ))}
                </Kutu>
              )}

              {(icerik.notlar || []).length > 0 && (
                <Kutu baslik="Modelin notları">
                  <ul className="space-y-1 text-[13px] text-slate-600">
                    {icerik.notlar.map((n, i) => <li key={i}>• {n}</li>)}
                  </ul>
                </Kutu>
              )}
            </>
          )}
        </div>

        {/* ══════════ Sağ: denetim + çıktı ══════════ */}
        <div className="space-y-4">
          {denetim && (
            <Kutu baslik="Denetim"
              sag={<span className={`rounded-md px-2 py-0.5 text-[12px] font-bold ${
                denetim.puan >= 85 ? 'bg-emerald-100 text-emerald-700'
                  : denetim.puan >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'
              }`}>{denetim.puan}/100</span>}
            >
              <p className="mb-2 text-[12px] text-slate-500">
                Modelin iddiası değil, metnin kendisi ölçülüyor. Düzenledikçe yeniden çalışır.
              </p>
              <ul className="space-y-1.5">
                {denetim.kurallar.map((k, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[12px]">
                    {k.durum === 'gecti' ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                      : k.durum === 'uyari' ? <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                        : <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500" />}
                    <span className="min-w-0">
                      <span className="font-medium text-slate-700">{k.ad}</span>
                      <span className="block text-slate-500">{k.detay}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Kutu>
          )}

          {icerik && (
            <Kutu baslik="Çıktı">
              <div className="space-y-2">
                <button
                  onClick={async () => { await panoyaKopyala(ciktiHtml); toast.success('CMS bloğu kopyalandı'); }}
                  className="flex w-full items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
                ><Copy className="h-3.5 w-3.5" /> CMS bloğunu kopyala</button>
                <button
                  onClick={() => indir(ciktiHtml, `${(brief?.olgular?.ofisSlug || 'ofis')}.html`, 'text/html;charset=utf-8')}
                  className="flex w-full items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
                ><Download className="h-3.5 w-3.5" /> HTML indir</button>
                {jsonLd && (
                  <button
                    onClick={async () => { await panoyaKopyala(jsonLd); toast.success('FAQPage şeması kopyalandı'); }}
                    className="flex w-full items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
                  ><Code2 className="h-3.5 w-3.5" /> FAQPage JSON-LD kopyala</button>
                )}
              </div>
              <p className="mt-2 text-[12px] leading-relaxed text-slate-500">
                HTML, sitedeki zengin metin kalıbıyla üretiliyor
                (<code className="rounded bg-slate-100 px-1">h2 &gt; strong</code>,
                {' '}<code className="rounded bg-slate-100 px-1">p &gt; span</code>). JSON-LD gövdeye
                gömülmez, tema/head alanına ayrı konur.
              </p>

              <div className="mt-3 border-t border-slate-100 pt-3">
                <label className="mb-1 block text-[11px] uppercase tracking-wide text-slate-400">Durum</label>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={kayitDurumu}
                    onChange={(e) => setKayitDurumu(e.target.value)}
                    className="flex-1 rounded-lg border border-slate-200 px-2 py-2 text-[12px] text-slate-700 outline-none focus:border-emerald-400"
                  >
                    {DURUMLAR.map((d) => <option key={d.k} value={d.k}>{d.ad}</option>)}
                  </select>
                  <button
                    onClick={() => kaydet(kayitDurumu)}
                    disabled={kaydediliyor}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {kaydediliyor ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    Kaydet
                  </button>
                </div>
                <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">
                  “Yayında” yalnızca <strong>Müşteri onayladı</strong> durumundan sonra seçilebilir;
                  uç, onaydan geçmemiş taslağı reddediyor.
                </p>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {taslakId && (
                  <button onClick={() => taslakSil(taslakId)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-[12px] font-semibold text-rose-600 hover:bg-rose-50">
                    <Trash2 className="h-3.5 w-3.5" /> Sil
                  </button>
                )}
              </div>
              {model && <p className="mt-2 text-[11px] text-slate-400">Model: {model}</p>}
            </Kutu>
          )}

          {!!(boot?.taslaklar || []).length && (
            <Kutu baslik={`Yazılanlar (${boot.taslaklar.length})`}>
              <div className="max-h-[300px] space-y-1 overflow-y-auto">
                {boot.taslaklar.map((t) => (
                  <button key={t.id} onClick={() => taslakAc(t.id)}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-slate-50">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[12px] font-medium text-slate-700">{t.ofisAd}</span>
                      <span className="text-[11px] text-slate-400">{t.sehir} · {nf(t.kelime)} kel.</span>
                    </span>
                    {t.puan != null && (
                      <span className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-bold ${
                        t.puan >= 85 ? 'bg-emerald-100 text-emerald-700'
                          : t.puan >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'
                      }`}>{t.puan}</span>
                    )}
                  </button>
                ))}
              </div>
            </Kutu>
          )}
        </div>
      </div>
    </div>
  );
}
